import { browser } from "$app/environment";
import { writable } from "svelte/store";

const STORAGE_KEY = "autopaylot-theme";

export type ThemeMode = "light" | "dark" | "system";

function getInitialMode(): ThemeMode {
	if (!browser) return "system";
	const stored = localStorage.getItem(STORAGE_KEY);
	if (stored === "dark" || stored === "light" || stored === "system") return stored;
	return "system";
}

function resolveTheme(mode: ThemeMode): "light" | "dark" {
	if (mode === "system") {
		return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
	}
	return mode;
}

function applyMode(mode: ThemeMode) {
	if (!browser) return;
	document.documentElement.classList.toggle("dark", resolveTheme(mode) === "dark");
}

const CYCLE: ThemeMode[] = ["light", "dark", "system"];

function createThemeStore() {
	const { subscribe, set, update } = writable<ThemeMode>("system");

	// Keep system mode in sync if the OS preference changes while app is open.
	let mediaQuery: MediaQueryList | null = null;

	return {
		subscribe,
		init() {
			const mode = getInitialMode();
			set(mode);
			applyMode(mode);

			if (browser) {
				mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
				mediaQuery.addEventListener("change", () => {
					// Only re-apply if we're in system mode.
					update((current) => {
						if (current === "system") applyMode("system");
						return current;
					});
				});
			}
		},
		toggle() {
			update((current) => {
				const next = CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
				applyMode(next);
				localStorage.setItem(STORAGE_KEY, next);
				return next;
			});
		},
	};
}

export const theme = createThemeStore();

/** @deprecated use theme.init() */
export function initThemeClient(): void {
	theme.init();
}
